import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Chip,
  Grid,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import { ConfirmDialog, TableSkeleton, ActionButton, DataTable, FilterBar } from '../components';

const MotionBox = motion(Box);

function ExternalAPIs() {
  const [externalApis, setExternalApis] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, apiId: null });
  const [editingApi, setEditingApi] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [formData, setFormData] = useState({
    scope: 2,
    project_id: null,
    api_name: '',
    api_code: '',
    provider: '',
    category: '',
    api_type: 1,
    method: 'GET',
    protocol: 2,
    base_url: '',
    environment: 1,
    auth_required: true,
    auth_strategy: null,
    timeout_ms: 10000,
    retry_count: 0,
    rate_limit_per_min: null,
    default_headers: '',
    auth_credentials: '',
    auth_config: '',
    query_params: '',
    path_params: '',
    request_body_template: '',
    response_mapping: '',
    description: '',
    documentation_url: '',
    status: 1,
  });

  const API_TYPES = {
    1: 'REST',
    2: 'SOAP',
    3: 'GRAPHQL',
    4: 'WEBHOOK',
  };

  const ENVIRONMENTS = {
    1: 'DEV',
    2: 'UAT',
    3: 'PROD',
  };

  const PROTOCOLS = {
    1: 'HTTP',
    2: 'HTTPS',
  };

  const AUTH_STRATEGIES = {
    1: 'API_KEY',
    2: 'OAUTH2',
    3: 'BASIC',
    4: 'HMAC',
    5: 'BEARER',
  };

  const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

  const SCOPES = {
    1: 'GLOBAL',
    2: 'PROJECT',
    3: 'TENANT',
  };

  const fetchExternalApis = async () => {
    try {
      setLoading(true);
      const [apisResponse, projectsResponse] = await Promise.all([
        API.externalApis.list(),
        API.projects.list()
      ]);
      setExternalApis(apisResponse?.data || []);
      setProjects(projectsResponse?.data || []);
    } catch (err) {
      showError('Failed to load external APIs');
      console.error('External APIs fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExternalApis();
  }, []);

  const handleOpenDialog = (api = null) => {
    if (api) {
      setEditingApi(api);
      setFormData({
        scope: api.scope || 2,
        project_id: api.project_id || null,
        api_name: api.api_name || '',
        api_code: api.api_code || '',
        provider: api.provider || '',
        category: api.category || '',
        api_type: api.api_type || 1,
        method: api.method || 'GET',
        protocol: api.protocol || 2,
        base_url: api.base_url || '',
        environment: api.environment || 1,
        auth_required: api.auth_required !== undefined ? api.auth_required : true,
        auth_strategy: api.auth_strategy || null,
        timeout_ms: api.timeout_ms || 10000,
        retry_count: api.retry_count || 0,
        rate_limit_per_min: api.rate_limit_per_min || null,
        default_headers: api.default_headers ? JSON.stringify(api.default_headers, null, 2) : '',
        auth_credentials: api.auth_credentials ? JSON.stringify(api.auth_credentials, null, 2) : '',
        auth_config: api.auth_config ? JSON.stringify(api.auth_config, null, 2) : '',
        query_params: api.query_params ? JSON.stringify(api.query_params, null, 2) : '',
        path_params: api.path_params ? JSON.stringify(api.path_params, null, 2) : '',
        request_body_template: api.request_body_template ? JSON.stringify(api.request_body_template, null, 2) : '',
        response_mapping: api.response_mapping ? JSON.stringify(api.response_mapping, null, 2) : '',
        description: api.description || '',
        documentation_url: api.documentation_url || '',
        status: api.status !== undefined ? api.status : 1,
      });
    } else {
      setEditingApi(null);
      setFormData({
        scope: 2,
        project_id: null,
        api_name: '',
        api_code: '',
        provider: '',
        category: '',
        api_type: 1,
        method: 'GET',
        protocol: 2,
        base_url: '',
        environment: 1,
        auth_required: true,
        auth_strategy: null,
        timeout_ms: 10000,
        retry_count: 0,
        rate_limit_per_min: null,
        default_headers: '',
        auth_credentials: '',
        auth_config: '',
        query_params: '',
        path_params: '',
        request_body_template: '',
        response_mapping: '',
        description: '',
        documentation_url: '',
        status: 1,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingApi(null);
    setFormData({
      scope: 2,
      project_id: null,
      api_name: '',
      api_code: '',
      provider: '',
      category: '',
      api_type: 1,
      method: 'GET',
      protocol: 2,
      base_url: '',
      environment: 1,
      auth_required: true,
      auth_strategy: null,
      timeout_ms: 10000,
      retry_count: 0,
      rate_limit_per_min: null,
      default_headers: '',
      auth_credentials: '',
      auth_config: '',
      query_params: '',
      path_params: '',
      request_body_template: '',
      response_mapping: '',
      description: '',
      documentation_url: '',
      status: 1,
    });
  };

  const handleSubmit = async () => {
    try {
      // Clean up the data before sending
      const submitData = { ...formData };
      
      // Parse JSON fields
      const jsonFields = ['default_headers', 'auth_credentials', 'auth_config', 'query_params', 'path_params', 'request_body_template', 'response_mapping'];
      jsonFields.forEach(field => {
        if (submitData[field] && typeof submitData[field] === 'string' && submitData[field].trim()) {
          try {
            submitData[field] = JSON.parse(submitData[field]);
          } catch (e) {
            throw new Error(`Invalid JSON in ${field.replace(/_/g, ' ')}: ${e.message}`);
          }
        } else {
          delete submitData[field];
        }
      });
      
      // Remove empty values
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === null || submitData[key] === '') {
          delete submitData[key];
        }
      });

      if (editingApi) {
        // Remove api_code from update payload as it's not allowed to be updated
        delete submitData.api_code;
        await API.externalApis.update(editingApi.api_id, submitData);
        showSuccess('External API updated successfully!');
      } else {
        await API.externalApis.create(submitData);
        showSuccess('External API created successfully!');
      }
      handleCloseDialog();
      fetchExternalApis();
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 
        (editingApi ? 'Failed to update external API' : 'Failed to create external API');
      showError(errorMessage);
      console.error('External API submit error:', err);
    }
  };

  const handleDeleteClick = (row) => {
    setConfirmDialog({ open: true, apiId: row.api_id });
  };

  const handleDelete = async () => {
    try {
      await API.externalApis.deactivate(confirmDialog.apiId);
      showSuccess('External API deleted successfully!');
      fetchExternalApis();
    } catch (err) {
      showError('Failed to delete external API');
      console.error('External API delete error:', err);
    } finally {
      setConfirmDialog({ open: false, apiId: null });
    }
  };

  const handleStatusToggle = async (apiId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await API.externalApis.update(apiId, { status: newStatus });
      showSuccess(`External API ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
      fetchExternalApis();
    } catch (err) {
      showError('Failed to update external API status');
      console.error('External API status update error:', err);
    }
  };

  const filteredApis = externalApis.filter(api => {
    const matchesSearch = 
      api.api_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      api.api_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      api.provider?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || api.status === parseInt(statusFilter);
    const matchesCategory = categoryFilter === 'all' || api.category === categoryFilter;
    const matchesEnvironment = environmentFilter === 'all' || api.environment === parseInt(environmentFilter);
    
    return matchesSearch && matchesStatus && matchesCategory && matchesEnvironment;
  });

  const uniqueCategories = [...new Set(externalApis.map(api => api.category).filter(Boolean))];

  const getApiTypeColor = (type) => {
    const colors = { 1: 'primary', 2: 'secondary', 3: 'info', 4: 'warning' };
    return colors[type] || 'default';
  };

  const getEnvironmentColor = (env) => {
    const colors = { 1: 'info', 2: 'warning', 3: 'error' };
    return colors[env] || 'default';
  };

  const getMethodColor = (method) => {
    const colors = {
      GET: 'success',
      POST: 'primary',
      PUT: 'warning',
      PATCH: 'info',
      DELETE: 'error',
    };
    return colors[method] || 'default';
  };

  const columns = [
    {
      field: 'api_name',
      headerName: 'API Name',
      width: '18%',
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {row.api_name}
          </Typography>
          {row.provider && (
            <Typography variant="caption" color="text.secondary">
              {row.provider}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'api_code',
      headerName: 'Code',
      width: '10%',
      render: (row) => (
        <Chip 
          label={row.api_code} 
          size="small" 
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'api_type',
      headerName: 'Type',
      width: '8%',
      render: (row) => (
        <Chip 
          label={API_TYPES[row.api_type]} 
          size="small" 
          color={getApiTypeColor(row.api_type)}
        />
      ),
    },
    {
      field: 'method',
      headerName: 'Method',
      width: '8%',
      render: (row) => (
        <Chip 
          label={row.method || 'N/A'} 
          size="small" 
          color={getMethodColor(row.method)}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'environment',
      headerName: 'Environment',
      width: '10%',
      render: (row) => (
        <Chip 
          label={ENVIRONMENTS[row.environment]} 
          size="small" 
          color={getEnvironmentColor(row.environment)}
        />
      ),
    },
    {
      field: 'base_url',
      headerName: 'Base URL',
      width: '20%',
      render: (row) => (
        <Typography 
          variant="caption" 
          sx={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: 'block'
          }}
          title={row.base_url}
        >
          {row.base_url}
        </Typography>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: '10%',
      render: (row) => row.category || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: '8%',
      sortable: false,
      render: (row) => (
        <Switch
          checked={row.status === 1}
          onChange={() => handleStatusToggle(row.api_id, row.status)}
          size="small"
          color="primary"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: '10%',
      render: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: '1', label: 'Active' },
        { value: '0', label: 'Inactive' },
      ],
    },
    {
      label: 'Environment',
      value: environmentFilter,
      onChange: setEnvironmentFilter,
      options: [
        { value: 'all', label: 'All Environments' },
        { value: '1', label: 'DEV' },
        { value: '2', label: 'UAT' },
        { value: '3', label: 'PROD' },
      ],
    },
    {
      label: 'Category',
      value: categoryFilter,
      onChange: setCategoryFilter,
      options: [
        { value: 'all', label: 'All Categories' },
        ...uniqueCategories.map(category => ({ value: category, label: category })),
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            External APIs
          </Typography>
        </Box>
        <TableSkeleton columns={7} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            External APIs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage external API integrations and configurations
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          endIcon={null}
          onClick={() => handleOpenDialog()}
        >
          Add External API
        </ActionButton>
      </Box>

      <FilterBar
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search APIs..."
        filters={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredApis}
        onEdit={handleOpenDialog}
        onDelete={handleDeleteClick}
        onView={null}
        getRowId={(row) => row.api_id}
        emptyMessage="No external APIs found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingApi ? 'Edit External API' : 'Add New External API'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Name"
                value={formData.api_name}
                onChange={(e) => setFormData({ ...formData, api_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Code"
                value={formData.api_code}
                onChange={(e) => setFormData({ ...formData, api_code: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Provider"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                placeholder="e.g., Google, AWS, Stripe"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Payment, Storage, Analytics"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Scope"
                value={formData.scope}
                onChange={(e) => {
                  const newScope = parseInt(e.target.value);
                  setFormData({ 
                    ...formData, 
                    scope: newScope,
                    project_id: newScope === 2 ? formData.project_id : null
                  });
                }}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {Object.entries(SCOPES).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            {formData.scope === 2 && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Project"
                  value={formData.project_id || ''}
                  onChange={(e) => setFormData({ ...formData, project_id: e.target.value || null })}
                  required
                  helperText="Required when scope is PROJECT"
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                >
                  <MenuItem value="">
                    <em>Select a project</em>
                  </MenuItem>
                  {projects.filter(p => p.status === 1).map((project) => (
                    <MenuItem key={project.project_id} value={project.project_id}>
                      {project.project_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="API Type"
                value={formData.api_type}
                onChange={(e) => setFormData({ ...formData, api_type: parseInt(e.target.value) })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {Object.entries(API_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Method"
                value={formData.method}
                onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {METHODS.map((method) => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Protocol"
                value={formData.protocol}
                onChange={(e) => setFormData({ ...formData, protocol: parseInt(e.target.value) })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {Object.entries(PROTOCOLS).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Environment"
                value={formData.environment}
                onChange={(e) => setFormData({ ...formData, environment: parseInt(e.target.value) })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                {Object.entries(ENVIRONMENTS).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Base URL"
                value={formData.base_url}
                onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                required
                type="url"
                placeholder="https://api.example.com/v1"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Auth Strategy"
                value={formData.auth_strategy || ''}
                onChange={(e) => setFormData({ ...formData, auth_strategy: e.target.value ? parseInt(e.target.value) : null })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="">None</MenuItem>
                {Object.entries(AUTH_STRATEGIES).map(([key, value]) => (
                  <MenuItem key={key} value={parseInt(key)}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Timeout (ms)"
                type="number"
                value={formData.timeout_ms}
                onChange={(e) => setFormData({ ...formData, timeout_ms: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Retry Count"
                type="number"
                value={formData.retry_count}
                onChange={(e) => setFormData({ ...formData, retry_count: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rate Limit (per min)"
                type="number"
                value={formData.rate_limit_per_min || ''}
                onChange={(e) => setFormData({ ...formData, rate_limit_per_min: e.target.value ? parseInt(e.target.value) : null })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Documentation URL"
                value={formData.documentation_url}
                onChange={(e) => setFormData({ ...formData, documentation_url: e.target.value })}
                type="url"
                placeholder="https://docs.example.com/api"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                Advanced Configuration (JSON Format)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Default Headers"
                value={formData.default_headers}
                onChange={(e) => setFormData({ ...formData, default_headers: e.target.value })}
                multiline
                rows={3}
                placeholder='{"Content-Type": "application/json"}'
                helperText="JSON object for default HTTP headers"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Auth Credentials"
                value={formData.auth_credentials}
                onChange={(e) => setFormData({ ...formData, auth_credentials: e.target.value })}
                multiline
                rows={3}
                placeholder='{"api_key": "your-key", "token": "your-token"}'
                helperText="JSON object for authentication credentials"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Auth Config"
                value={formData.auth_config}
                onChange={(e) => setFormData({ ...formData, auth_config: e.target.value })}
                multiline
                rows={3}
                placeholder='{"location": "header", "key_name": "Authorization", "prefix": "Bearer "}'
                helperText="JSON object for auth configuration"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Query Parameters"
                value={formData.query_params}
                onChange={(e) => setFormData({ ...formData, query_params: e.target.value })}
                multiline
                rows={3}
                placeholder='{"limit": 10, "offset": 0}'
                helperText="JSON object for default query params"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Path Parameters"
                value={formData.path_params}
                onChange={(e) => setFormData({ ...formData, path_params: e.target.value })}
                multiline
                rows={3}
                placeholder='{"id": "{id}", "version": "v1"}'
                helperText="JSON object for URL path parameters"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Request Body Template"
                value={formData.request_body_template}
                onChange={(e) => setFormData({ ...formData, request_body_template: e.target.value })}
                multiline
                rows={3}
                placeholder='{"data": {}, "metadata": {}}'
                helperText="JSON template for request body"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Response Mapping"
                value={formData.response_mapping}
                onChange={(e) => setFormData({ ...formData, response_mapping: e.target.value })}
                multiline
                rows={3}
                placeholder='{"data_path": "data", "error_path": "error", "success_path": "success"}'
                helperText="JSON object for response data extraction"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: parseInt(e.target.value) })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" startIcon={null} endIcon={null} onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" startIcon={null} endIcon={null} onClick={handleSubmit}>
            {editingApi ? 'Update API' : 'Create API'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, apiId: null })}
        onConfirm={handleDelete}
        title="Delete External API"
        message="Are you sure you want to delete this external API? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default ExternalAPIs;
